import React, { useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface BulkPdfSelectionBarProps {
  alignToRef?: React.RefObject<HTMLElement>;
  selectedCount: number;
  maxCount: number | null;
  isDownloadDisabled: boolean;
  onClearAll: () => void;
  onCancel: () => void;
  onDownload: () => void;
}

export function BulkPdfSelectionBar({
  alignToRef,
  selectedCount,
  maxCount,
  isDownloadDisabled,
  onClearAll,
  onCancel,
  onDownload,
}: BulkPdfSelectionBarProps) {
  const { t } = useTranslation();
  const [alignedBounds, setAlignedBounds] = useState<{
    left: number;
    width: number;
  } | null>(null);

  useLayoutEffect(() => {
    const alignTarget = alignToRef?.current;
    if (!alignTarget) return undefined;

    const syncBoundsToTarget = () => {
      const { left, width } = alignTarget.getBoundingClientRect();
      setAlignedBounds({ left, width });
    };

    syncBoundsToTarget();
    const resizeObserver = new ResizeObserver(syncBoundsToTarget);
    resizeObserver.observe(alignTarget);
    window.addEventListener("resize", syncBoundsToTarget);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", syncBoundsToTarget);
    };
  }, [alignToRef]);

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: { xs: 16, sm: 24 },
        ...(alignedBounds
          ? {
              left: alignedBounds.left,
              width: alignedBounds.width,
              boxSizing: "border-box",
            }
          : {
              left: { xs: 16, sm: 48 },
              right: { xs: 16, sm: 48 },
            }),
        zIndex: 1200,
        backgroundColor: "#f2f2f2",
        border: "1px solid #e0e0e0",
        borderRadius: "28px",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        justifyContent: "space-between",
        gap: { xs: 1.5, sm: 2 },
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 0.25, sm: 1.5 },
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#333" }}>
          {t("bulkPdfExport")}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#696969" }}>
          {t("bulkPdfSelectedCount", { count: selectedCount })}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#696969" }}>
          {t("bulkPdfMaxCount", { max: maxCount ?? "-" })}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 1, sm: 1.5 },
        }}
      >
        <Button
          className="btn btn-no-border-new"
          sx={{ whiteSpace: "nowrap" }}
          startIcon={<CloseIcon sx={{ fontSize: "1.1rem" }} />}
          onClick={onClearAll}
        >
          {t("bulkPdfClearAll")}
        </Button>
        <Button
          className="btn btn-no-border"
          sx={{ whiteSpace: "nowrap" }}
          onClick={onCancel}
        >
          {t("dialogReport.cancel")}
        </Button>
        <Button
          className="btn btn-primary"
          sx={{ whiteSpace: "nowrap" }}
          disabled={isDownloadDisabled}
          onClick={onDownload}
        >
          {t("translations.download")}
        </Button>
      </Box>
    </Box>
  );
}
